import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  writeBatch,
  type Firestore,
} from 'firebase/firestore';
import type {
  Activity,
  ActivityRepository,
  CreateActivityInput,
  Unsubscribe,
  UpdateActivityInput,
} from '@nx/domain';
import { mapActivityDoc, snapshotData } from './firestoreMappers';

function activitiesCol(db: Firestore, eventId: string) {
  return collection(db, 'events', eventId, 'activities');
}

export class FirebaseActivityRepository implements ActivityRepository {
  constructor(private readonly db: Firestore) {}

  async listByEvent(eventId: string): Promise<Activity[]> {
    return new Promise((resolve, reject) => {
      const unsub = onSnapshot(
        activitiesCol(this.db, eventId),
        (snap) => {
          unsub();
          resolve(snap.docs.map((d) => mapActivityDoc(eventId, d.id, snapshotData(d))));
        },
        reject,
      );
    });
  }

  watchByEvent(eventId: string, listener: (activities: Activity[]) => void): Unsubscribe {
    return onSnapshot(activitiesCol(this.db, eventId), (snap) => {
      listener(snap.docs.map((d) => mapActivityDoc(eventId, d.id, snapshotData(d))));
    });
  }

  async create(eventId: string, input: CreateActivityInput): Promise<Activity> {
    const ref = await addDoc(activitiesCol(this.db, eventId), {
      date: input.date,
      title: input.title.trim(),
      type: input.type,
      startTime: input.startTime,
      endTime: input.endTime,
      venue: input.venue?.trim() ?? null,
      partnerRefs: input.partnerRefs ?? [],
      sortOrder: input.sortOrder,
      createdAt: serverTimestamp(),
    });
    const snap = await this.listByEvent(eventId);
    const created = snap.find((a) => a.id === ref.id);
    if (!created) throw new Error('Activity not found after create');
    return created;
  }

  async update(
    eventId: string,
    activityId: string,
    input: UpdateActivityInput,
  ): Promise<Activity> {
    const patch: Record<string, unknown> = {};
    if (input.date !== undefined) patch.date = input.date;
    if (input.title !== undefined) patch.title = input.title.trim();
    if (input.type !== undefined) patch.type = input.type;
    if (input.startTime !== undefined) patch.startTime = input.startTime;
    if (input.endTime !== undefined) patch.endTime = input.endTime;
    if (input.venue !== undefined) patch.venue = input.venue?.trim() ?? null;
    if (input.partnerRefs !== undefined) patch.partnerRefs = input.partnerRefs;
    if (input.sortOrder !== undefined) patch.sortOrder = input.sortOrder;
    await updateDoc(doc(this.db, 'events', eventId, 'activities', activityId), patch);
    const snap = await this.listByEvent(eventId);
    const updated = snap.find((a) => a.id === activityId);
    if (!updated) throw new Error('Activity not found after update');
    return updated;
  }

  async delete(eventId: string, activityId: string): Promise<void> {
    await deleteDoc(doc(this.db, 'events', eventId, 'activities', activityId));
  }

  async reorder(eventId: string, date: string, orderedIds: string[]): Promise<void> {
    const batch = writeBatch(this.db);
    orderedIds.forEach((id, index) => {
      batch.update(doc(this.db, 'events', eventId, 'activities', id), {
        sortOrder: index,
        date,
      });
    });
    await batch.commit();
  }
}
